<template>
    <label
        class="text-sm font-semibold text-secondary-600 block mb-1"
    >
        <slot name="label" />
        <el-form-item :prop="prop" v-if="withFormItem">
            <el-select
                :disabled="isDisabled"
                :multiple="isMultiple"
                ref="select"
                class="w-full"
                v-model="valueProxy"
                clearable
                :filterable="filterable"
                :allow-create="allowCreate"
                :maxlength="maxlength"
                :placeholder="placeholder"
                :loading="loading"
                :remote="remote"
                :remote-method="remoteMethod" 
                :reserve-keyword="reserveKeyword"
                :size="size"
                @focus="handleFocus"
                @blur="handleBlur"
                @visible-change="handleDropdownVisible"
            >
                <template v-if="group">
                    <el-option-group
                        v-for="optionGroup in normalizedOptions"
                        :key="optionGroup.label"
                        :label="optionGroup.label"
                    >
                        <el-option
                          v-for="option in optionGroup.options"
                          :key="option.id"
                          :label="option.name"
                          :value="option.value"
                          @change="onChange"
                          @select="onSelect"
                        />
                    </el-option-group>
                </template>
                <template v-else>
                    <el-option 
                        v-for="option in normalizedOptions"
                        :key="option.id"
                        :label="option.name"
                        :value="option.value"
                        @change="onChange"
                        @select="onSelect"
                    />
                </template>
            </el-select>
        </el-form-item>
        <el-select v-else
            :disabled="isDisabled"
            :multiple="isMultiple"
            ref="select"
            class="w-full"
            v-model="valueProxy"
            clearable
            :filterable="filterable"
            :allow-create="allowCreate"
            :maxlength="maxlength"
            :placeholder="placeholder"
            :loading="loading"
            :remote="remote"
            :remote-method="remoteMethod" 
            :reserve-keyword="reserveKeyword"
            :size="size"
            @focus="handleFocus"
            @blur="handleBlur"
            @visible-change="handleDropdownVisible"
        >
            <template v-if="group">
                <el-option-group
                    v-for="optionGroup in normalizedOptions"
                    :key="optionGroup.label"
                    :label="optionGroup.label"
                >
                    <el-option
                        v-for="option in optionGroup.options"
                        :key="option.id"
                        :label="option.name"
                        :value="option.value"
                        @change="onChange"
                        @select="onSelect"
                    />
                </el-option-group>
            </template>
            <template v-else>
                <el-option
                    v-for="option in normalizedOptions"
                    :key="option.id"
                    :label="option.name"
                    :value="option.value"
                    @change="onChange"
                    @select="onSelect"
                />
            </template>
        </el-select>
    </label>
</template>

<script>
export default {
  name: 'AccessibleSelect',
  props: {
    value: [String, Number, null],
    prop: { 
      type: String,
      required: true 
    },
    options: { // [{ id, value, name }] or [{ label, options: [{ id, value, name }] }] when group=true
      type: Array, 
      required: true 
    },
    group: {
      type: Boolean,
      default: false
    }, 
    idPrefix: { 
      type: String, 
      default: 'select' 
    },
    withFormItem: {
      type: Boolean,
      default: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isMultiple: {
      type: Boolean,
      default: false,
    },
    allowCreate: {
      type: Boolean,
      default: false,
    },
    maxlength: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    remote: {
      type: Boolean,
      default: false,
    },
    remoteMethod: {
      type: Function,
      default: null,
    },
    reserveKeyword: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: null,
    }
  },
  data () {
    return {
      filterable: false,
      observer: null
    }
  },
  mounted () {
    const input = this.$refs.select?.$el.querySelector('input')
    if (input) {
      input.setAttribute('role', 'combobox')
      input.setAttribute('aria-haspopup', 'listbox')
      input.setAttribute('aria-expanded', 'false')
    }
  },
  computed: {
    valueProxy: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    labelId () {
      return `${this.idPrefix}-${this.prop}-label`
    },
    normalizedOptions () {
      if (this.group) {
        // Para opciones agrupadas: [{ label, options: [{ id, value, name }] }]
        return this.options.map(optionGroup => ({
          ...optionGroup,
          options: optionGroup.options.map(option => ({
            ...option,
            value: option.value !== undefined ? option.value : option.id
          }))
        }))
      } else {
        // Para opciones simples: [{ id, value, name }]
        return this.options.map(option => ({
          ...option,
          value: option.value !== undefined ? option.value : option.id
        }))
      }
    }
  },
  methods: {
    onChange (item) {
      this.$emit('change', item)
    },
    onSelect (item) {
      console.log('select', item)
      
      this.$emit('select', item)
    },
    handleFocus () {
      setTimeout(() => {
        this.filterable = true
      }, 200)
    },
    handleBlur () {
      this.filterable = false
    },
    handleDropdownVisible (visible) {
      if (!visible) {
        if (this.observer) {
          this.observer.disconnect()
          this.observer = null
        }
        return
      }

      this.$nextTick(() => {
        const dropdowns = Array.from(document.querySelectorAll('.el-select-dropdown'))
        const visibleDropdown = dropdowns.reverse().find(dd =>
          dd.style.display !== 'none' && dd.querySelector('.el-select-dropdown__list')
        )
        if (!visibleDropdown) return

        const list = visibleDropdown.querySelector('.el-select-dropdown__list')
        if (!list) return

        list.setAttribute('role', 'listbox')

        // Handle group headers and options
        const groupHeaders = list.querySelectorAll('.el-select-group__title')
        groupHeaders.forEach((header, index) => {
          header.setAttribute('role', 'group')
          header.setAttribute('id', `${this.prop}-group-${index}`)
        })

        const items = list.querySelectorAll('.el-select-dropdown__item')
        items.forEach((item, index) => {
          item.setAttribute('role', 'option')
          item.setAttribute('id', `${this.prop}-option-${index}`)
          const text = item.innerText.trim()
          const isSelected = text === this.getLabelByValue(this.valueProxy)
          item.setAttribute('aria-selected', isSelected.toString())
        })

        const input = this.$refs.select?.$el.querySelector('input')
        if (!input) return
        

        const selectedIndex = this.findOptionIndex(this.valueProxy)
        if (selectedIndex >= 0) {
          input.setAttribute('aria-activedescendant', `${this.prop}-option-${selectedIndex}`)
        }

        this.observer = new MutationObserver(() => {
          const active = list.querySelector('.el-select-dropdown__item.hover')
          if (active) {
            input.setAttribute('aria-activedescendant', active.id)
          }
        })
        this.observer.observe(list, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class']
        })
      })
    },
    getLabelByValue (value) {
      if (this.group) {
        for (const optionGroup of this.normalizedOptions) {
          const opt = optionGroup.options.find(o => o.value === value)
          if (opt) return opt.name
        }
        return ''
      } else {
        const opt = this.normalizedOptions.find(o => o.value === value)
        return opt ? opt.name : ''
      }
    },
    findOptionIndex (value) {
      if (this.group) {
        let index = 0
        for (const optionGroup of this.normalizedOptions) {
          for (const option of optionGroup.options) {
            if (option.value === value) {
              return index
            }
            index++
          }
        }
        return -1
      } else {
        return this.normalizedOptions.findIndex(opt => opt.value === value)
      }
    }
  },
  destroyed () {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }
}
</script>
